import { useState, useCallback } from "react";
import {
  Box,
  Card,
  Flex,
  Text,
  TextField,
  Slider,
  Badge,
  Separator,
  Callout,
} from "@radix-ui/themes";
import {
  calculateWater,
  calculateCoffee,
  calculateCups,
  DEFAULT_RATIO,
  CUP_SIZES,
} from "../lib/calculations";
import { useLanguage } from "./LanguageProvider";

type InputMode = "coffee" | "water";

export function CoffeeCalculator() {
  const { t } = useLanguage();
  const [coffee, setCoffee] = useState<number>(30);
  const [water, setWater] = useState<number>(() => calculateWater(30));
  const [ratio, setRatio] = useState<number>(DEFAULT_RATIO);
  const [lastEdited, setLastEdited] = useState<InputMode>("coffee");

  const handleCoffeeChange = useCallback(
    (value: string) => {
      const num = parseFloat(value);
      if (!value || isNaN(num)) {
        setCoffee(0);
        setWater(0);
        return;
      }
      // Limit to reasonable range (0-1000g)
      const validNum = Math.max(0, Math.min(1000, num));
      setCoffee(validNum);
      setWater(calculateWater(validNum, ratio));
      setLastEdited("coffee");
    },
    [ratio]
  );

  const handleWaterChange = useCallback(
    (value: string) => {
      const num = parseFloat(value);
      if (!value || isNaN(num)) {
        setWater(0);
        setCoffee(0);
        return;
      }
      // Limit to reasonable range (0-10000ml = 10L)
      const validNum = Math.max(0, Math.min(10000, num));
      setWater(validNum);
      setCoffee(calculateCoffee(validNum, ratio));
      setLastEdited("water");
    },
    [ratio]
  );

  const handleRatioChange = useCallback(
    (values: number[]) => {
      const newRatio = values[0];
      setRatio(newRatio);
      // Recalculate based on what was last edited
      if (lastEdited === "coffee") {
        setWater(calculateWater(coffee, newRatio));
      } else {
        setCoffee(calculateCoffee(water, newRatio));
      }
    },
    [coffee, water, lastEdited]
  );

  const cups = calculateCups(water, CUP_SIZES.medium);
  const ratioDisplay = Math.round(ratio * 1000);

  return (
    <Flex direction="column" gap="5">
      <Box className="hero-section">
        <Text as="p" size="3" color="gray" align="center" className="hero-text">
          {t("heroLine1")}
          <br />
          {t("heroLine2")}
        </Text>
      </Box>

      <Card size="3" className="calculator-card">
        <Flex direction="column" gap="5">
          <Flex gap="4" wrap="wrap" justify="center">
            <Box className="input-group" style={{ flex: "1", minWidth: "200px" }}>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <Text size="6" className="input-icon">
                    <CoffeeBeansIcon />
                  </Text>
                  <Text as="label" size="2" weight="bold" color="gray">
                    {t("coffee")}
                  </Text>
                </Flex>
                <TextField.Root
                  size="3"
                  type="number"
                  min="0"
                  step="1"
                  value={coffee || ""}
                  onChange={(e) => handleCoffeeChange(e.target.value)}
                  placeholder="0"
                  className="number-input"
                />
                <Text size="1" color="gray" align="center">
                  {t("grams")}
                </Text>
              </Flex>
            </Box>

            <Flex align="center" justify="center" py="4" className="equals-sign">
              <Text size="5" color="gray" weight="bold">
                =
              </Text>
            </Flex>

            <Box className="input-group" style={{ flex: "1", minWidth: "200px" }}>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <Text size="6" className="input-icon">
                    <WaterDropIcon />
                  </Text>
                  <Text as="label" size="2" weight="bold" color="gray">
                    {t("water")}
                  </Text>
                </Flex>
                <TextField.Root
                  size="3"
                  type="number"
                  min="0"
                  step="10"
                  value={water || ""}
                  onChange={(e) => handleWaterChange(e.target.value)}
                  placeholder="0"
                  className="number-input"
                />
                <Text size="1" color="gray" align="center">
                  {t("milliliters")}
                </Text>
              </Flex>
            </Box>
          </Flex>

          <Separator size="4" />

          <Box>
            <Flex justify="between" align="center" mb="2">
              <Text size="2" weight="medium">
                {t("brewingRatio")}
              </Text>
              <Badge variant="soft" size="2">
                {ratioDisplay}g / 1L
              </Badge>
            </Flex>
            <Slider
              value={[ratio]}
              onValueChange={handleRatioChange}
              min={0.04}
              max={0.08}
              step={0.002}
              aria-label={t("brewingRatio")}
            />
            <Flex justify="between" mt="1">
              <Text size="1" color="gray">
                {t("lighter")} (40g/L)
              </Text>
              <Text size="1" color="gray">
                {t("stronger")} (80g/L)
              </Text>
            </Flex>
          </Box>
        </Flex>
      </Card>

      <Card size="2" className="result-card">
        <Flex gap="4" justify="center" wrap="wrap">
          <ResultBadge
            icon={<CupIcon />}
            value={cups.toFixed(1)}
            label={`${t("cups")} (${t("cupSize")})`}
          />
          <ResultBadge
            icon={<ScaleIcon />}
            value={`1:${Math.round(1 / ratio)}`}
            label={t("ratio")}
          />
          <ResultBadge
            icon={<CoffeeBeansIcon />}
            value={`${ratioDisplay}g`}
            label={t("perLiter")}
          />
        </Flex>
      </Card>

      <Callout.Root color="brown" variant="soft" className="tip-callout">
        <Callout.Icon>
          <InfoIcon />
        </Callout.Icon>
        <Callout.Text>
          <Text weight="bold">{t("tipTitle")}</Text> {t("tipText")}
        </Callout.Text>
      </Callout.Root>
    </Flex>
  );
}

function ResultBadge({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
}) {
  return (
    <Flex direction="column" align="center" gap="1" className="result-badge">
      <Text color="gray" className="result-icon">
        {icon}
      </Text>
      <Text size="5" weight="bold">
        {value}
      </Text>
      <Text size="1" color="gray">
        {label}
      </Text>
    </Flex>
  );
}

function CoffeeBeansIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 12c0-3.5 2.5-6 6-6s6 2.5 6 6-2.5 6-6 6-6-2.5-6-6z" />
      <path d="M12 6c0 0-1 2-1 6s1 6 1 6" />
      <path d="M12 6c0 0 1 2 1 6s-1 6-1 6" />
    </svg>
  );
}

function WaterDropIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  );
}

function CupIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 8h1a4 4 0 1 1 0 8h-1" />
      <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
      <line x1="6" y1="2" x2="6" y2="4" />
      <line x1="10" y1="2" x2="10" y2="4" />
      <line x1="14" y1="2" x2="14" y2="4" />
    </svg>
  );
}

function ScaleIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 16h6" />
      <path d="M2 16h6" />
      <path d="m9 7 3-3 3 3" />
      <path d="M12 4v16" />
      <path d="m2 12 3-3 3 3" />
      <path d="m16 12 3-3 3 3" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}
